/**
 * Impact Radius affiliate script loader.
 *
 * NOTE FOR LOCAL TESTING: You will need to comment out `React.StrictMode`
 * from `react-render.tsx`, due to an issue with Helmet in local development
 * mode with React 19. Otherwise, the script won't be injected consistently.
 */

import { useEffect, useState } from 'react';

import { useWtrSelector, useWtrStore } from 'redux/hooks';
import { getIsInitialPageLoaded } from 'redux/modules/page/selectors';
import {
  identifyCustomer,
  shouldLoadImpact,
  shouldUseImpact,
  stashImpactValues,
  trackConversion,
} from './utils';

const Impact = () => {
  const hasMarketingConsent = useWtrSelector(state => state.cookies?.marketing);
  const isInitialPageLoaded = useWtrSelector(getIsInitialPageLoaded);
  const store = useWtrStore();

  // This state is used to enable the initial client-side render to match the server-side render,
  // in order to avoid hydration errors. After the initial render, we can then re-run the logic
  // utilising sessionStorage (which we only have access to on the client-side).
  const [isFirstRender, setIsFirstRender] = useState(true);

  // The reason for separating `includeScript` and `useImpact` is it allows us to test
  // Impact functions with Codecept, despite having `noExternals`. So in that case, the real
  // script is not loaded (`shouldLoadImpact === false`), but we should still be able to *use*
  // the mock version of the script (`shouldUseImpact === true`).
  const [includeScript, setIncludeScript] = useState(
    shouldLoadImpact(hasMarketingConsent, isFirstRender),
  );
  const [useImpact, setUseImpact] = useState(shouldUseImpact(hasMarketingConsent, isFirstRender));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFirstRender(false);
  }, []);

  useEffect(() => {
    if (!isFirstRender) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIncludeScript(shouldLoadImpact(hasMarketingConsent, false));
      setUseImpact(shouldUseImpact(hasMarketingConsent, false));
    }
  }, [isFirstRender, hasMarketingConsent]);

  useEffect(() => {
    // Execute the script from the document template, to initialise window.ire
    // and load the Impact script. We do it this way rather than rendering the script
    // with Helmet because it ensures the window.ire queuing system will be in place
    // before we try to fire Impact events.
    if (includeScript && window.initialiseImpact) {
      window.initialiseImpact();
      window.initialiseImpact = undefined;
    }
    // Execute Impact actions once Impact is active AND initial page data is loaded,
    // so we have access to the customer account data (i.e. email address) from the Redux store.
    if (useImpact && isInitialPageLoaded) {
      stashImpactValues();
      const { getState } = store;
      identifyCustomer(getState);
      trackConversion();
    }
  }, [includeScript, useImpact, isInitialPageLoaded, store]);

  return null;
};

export default Impact;
